// src/services/groupCalls.js — group calls in the browser.
//
// 🟢 THE WEB SPEAKS THE PHONE'S GROUP-CALL PROTOCOL, VERBATIM. Every event name
//    and payload shape below is taken from the app's own client
//    (allo-app/server/GroupWebRTCService.ts) and the server handlers it talks to
//    (server/functions/index.js). Nothing here is invented; where a rule looked
//    arbitrary it was read out of one of those two files and is quoted at the
//    place it matters.
//
//      start   → 'start-group-call' {groupId, groupName, participantIds,
//                                    callerName, callerImage, callType, fromUserId}
//      ring    ← 'incoming-group-call' {groupId, groupName, callerName, …}
//      ack     → 'group-call-ringing-ack' {groupId, userId}
//      join    → 'join-group-call'  {groupId, userId, userData}
//      decline → 'group-call-decline' {groupId, userId}
//      joined  ← 'group-user-joined' {newUserId, userData}
//      mesh    ⇄ 'group-offer' / 'group-answer' / 'group-ice-candidate'
//                 out: {…, toUserId, fromUserId}   in: {…, fromUserId}
//      state   ← 'group-call-state' {groupId, callerId, callType, invitees:{uid:{state}}}
//      left    ← 'group-user-left'  {userId}
//      leave   → 'leave-group-call' {groupId, userId}
//
// 🟥 IT IS A MESH, NOT AN SFU. One RTCPeerConnection per other participant, which
//    is what the phones do — so a browser is just another peer in the same room
//    rather than a second topology the server would have to bridge. The cost is
//    quadratic and real: this is why joinable size is capped below.

import {
  doc, setDoc, arrayUnion, addDoc, collection, onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { decide, groupInviteKey, groupInviteSeq, groupInviteLegacyReinvite } from './callResilience';
import { legBudget, nextScale, fpsFor, START_BPS } from './groupQuality';
import { db } from '../config/firebase';
import { getSocket } from './socket';
import { rememberCallRow, callRowFrom } from './localCallRows';
import { fetchIce } from './calls';
import { chatNotifySettings, startRingtone, stopRingtone } from './notify';

/* 🟥 A MESH GETS EXPENSIVE FAST — n−1 encoders and n−1 decoders per person. The
   phones carry the same shape, but a browser tab is also rendering the app
   around it. Past this we refuse to JOIN rather than join and stutter, because
   a call that degrades everyone's audio is worse than one person staying out. */
const MAX_MESH = 8;

const listeners = new Set();

const state = {
  status: 'idle',        // idle | ringing | joining | active
  groupId: null,
  groupName: '',
  callType: 'voice',     // 'voice' | 'video'
  callerId: null,
  callerName: '',
  callerImage: null,
  /* uid → { stream, name, image, muted } for everyone whose media we hold */
  peers: {},
  /* uid → 'calling' | 'ringing' | 'joined' | 'declined', straight from the
     server's own state broadcast — the tiles are its opinion, not ours */
  invitees: {},
  localStream: null,
  muted: false,
  cameraOff: false,
  startedAt: null,
  note: '',
  error: '',
  /* \U0001f7e5 THE SESSION ID IS NOT OURS TO INVENT. The in-call chat lives at
     chats/{id}/call_threads/{sessionId}/messages, and the phone takes that id
     from `activeCall.sessionId` — host writes it, everyone else reads it. A web
     client that generated its own would write into a thread nobody opens, and
     the two halves of one conversation would sit in different documents. */
  sessionId: null,
  /* the in-call chat, live for as long as the call is */
  chat: [],
  /* how many times each person may still be rung — the SERVER owns the count
     (MAX_GROUP_REINVITES in index.js) and puts it on every state broadcast, so
     re-inviting twice here and once on the phone really is three */
  maxInvites: 3,
};

function emit() { for (const fn of listeners) { try { fn(snapshot()); } catch { /* a bad subscriber is not this engine's problem */ } } }

/* 🟥 A SHALLOW COPY WOULD SHARE `peers`, so React would compare the same object
   with itself and skip the render that adds a participant tile. */
function snapshot() {
  return { ...state, peers: { ...state.peers }, invitees: { ...state.invitees }, chat: [...state.chat] };
}

export function subscribeGroupCalls(fn) { listeners.add(fn); fn(snapshot()); return () => listeners.delete(fn); }
export function getGroupCallState() { return snapshot(); }

/* ══ WHO MAY START ONE ══════════════════════════════════════════════════════
   🟥 THE SAME RULE THE APP ENFORCES, IN THE SAME WORDS. ChatModal:2824 reads
      `!isGroup || amIAdmin || permissions.makeCalls !== false`, and
      CallsHistoryScreen / NewCallModal both spell it
      `permissions.makeCalls === false && !admins.includes(me)`. Those are the
      same statement; this is it once, so the web cannot drift from the phone.
   🟢 `!== false` IS THE POINT: the field is absent on every group made before
      the permission existed, and absent means allowed. `=== true` would have
      silently taken calling away from every one of them. */
export function canStartGroupCall(chat, myUid) {
  if (!chat?.isGroup) return true;
  if ((chat.admins || []).includes(myUid)) return true;
  return chat?.permissions?.makeCalls !== false;
}

/** Why the button is off, in the words the person needs — or '' when it is on. */
export function groupCallBlockedReason(chat, myUid) {
  if (!chat?.isGroup) return '';
  if ((chat.leftParticipants || []).includes(myUid)) return 'You have left this group';
  if (!canStartGroupCall(chat, myUid)) return 'Only admins can start calls in this group';
  const n = (chat.participants || []).length;
  if (n > MAX_MESH) return `Group calls on the web are limited to ${MAX_MESH} people`;
  return '';
}

/* ══ THE MESH ═══════════════════════════════════════════════════════════════ */

let socket = null;
let myUid = null;
let myProfile = null;
const peers = new Map();          // uid → RTCPeerConnection
const pendingIce = new Map();     // uid → [candidate] held until the remote description lands
/* 🩺 uid → the reconnect bookkeeping for THAT leg. In a mesh every connection
   fails independently, so the coordinator is per-leg rather than per-call. */
const legs = new Map();
/* uid → the send profile currently applied to that leg */
const legState = new Map();
let rampTimer = null;
let bound = null;                 // the socket these listeners are attached to
const answered = new Set();       // dedup key → this ring has been dealt with
let ringKey = '';
let ringSeq = 0;                  // which ring of this call (1 = first; a re-invite is 2, 3…)
let ringTimer = null;
let chatUnsub = null;
/* \U0001f7e5 THE CALL WAS REALLY THERE ONCE. `activeCall` is written by the server a
   moment AFTER the caller emits start-group-call, so a dismissal rule of "no
   activeCall → end" would kill every call in the instant before it appeared.
   This latches once we have actually seen it. */
let sawActiveCall = false;
/* 🟥 ONLY THE HOST WRITES THE CALL'S STATUS MESSAGE, which is the phone's rule
   too — GroupCallScreen guards `saveGroupCallHistory` on having started the
   call. Every participant writing one would put four identical "started a
   call" bubbles in the conversation. */
let iAmHost = false;

/* \U0001f7e2 THE RINGTONE IS THE ONE THE PERSON CHOSE FOR THIS CHAT, and it is
   silent if they turned call alerts off there — the same per-chat settings the
   1:1 ring obeys, read from the same place the info pane writes them. */
function ring(on) {
  try {
    if (!on) { stopRingtone(); return; }
    const np = chatNotifySettings(state.groupId);
    if (np.muted || np.callAlerts === false) return;
    startRingtone(np.ringtone);
  } catch { /* a ringtone that will not play must not stop the call arriving */ }
}

const peerName = (uid) => state.peers[uid]?.name || '';

function setPeer(uid, patch) {
  state.peers[uid] = { name: '', image: null, muted: false, stream: null, ...(state.peers[uid] || {}), ...patch };
  emit();
}

function dropPeer(uid) {
  const pc = peers.get(uid);
  if (pc) { try { pc.close(); } catch { /* already closed */ } peers.delete(uid); }
  /* 🟥 A LEG'S TIMER MUST DIE WITH IT, or it keeps restarting ICE on a closed
     connection for a person who has left. */
  const leg = legs.get(uid);
  if (leg?.timer) { try { clearTimeout(leg.timer); } catch { /* gone */ } }
  legs.delete(uid);
  legState.delete(uid);
  pendingIce.delete(uid);
  delete state.peers[uid];
  emit();
}

/* ═══ 📶 THE UPLOAD BUDGET ═══════════════════════════════════════════════════
   🟥 THE BROWSER'S GROUP CALL HAD NO QUALITY MANAGEMENT AT ALL — not a weak one,
      none. In a mesh you send a SEPARATE copy of your camera to every other
      person, so a four-way call is three full-rate uploads from one laptop. The
      uplink saturates and the first thing to die is the audio everyone needs.
   🟢 A MESH CAN DO WHAT AN SFU CANNOT WITHOUT SIMULCAST: a different encoding
      per person. The pinned tile is worth more than six thumbnails nobody is
      looking at, and here that is just a different maxBitrate on that leg.     */
let pinnedUid = null;

/** The UI tells us which tile is large; the budget follows the eye. */
export function setPinnedPeer(uid) {
  pinnedUid = uid || null;
  for (const id of peers.keys()) applyLegBudget(id);
}

function tierFor(uid) {
  if (pinnedUid && uid === pinnedUid) return 'pinned';
  if (pinnedUid) return 'thumb';        // someone else is centre stage
  return 'normal';
}

async function applyLegBudget(uid, bpsOverride) {
  const pc = peers.get(uid);
  if (!pc) return;
  try {
    const sender = pc.getSenders().find((x) => x.track && x.track.kind === 'video');
    if (!sender) return;                 // voice-only leg — nothing to cap
    const prev = legState.get(uid) || { bps: START_BPS, scale: 4 };
    const ceiling = legBudget(peers.size, tierFor(uid));
    const bps = Math.max(60_000, Math.min(Number(bpsOverride) || prev.bps, ceiling));
    const scale = nextScale(bps, prev.scale);
    const params = sender.getParameters();
    if (!params.encodings || !params.encodings.length) params.encodings = [{ active: true }];
    params.encodings[0].maxBitrate = bps;
    params.encodings[0].scaleResolutionDownBy = scale;
    params.encodings[0].maxFramerate = fpsFor(scale);
    /* 🟥 THE BITRATE MUST LAND EVEN WHERE degradationPreference IS REJECTED —
       some browsers throw on the whole setParameters for that one key, which
       would silently drop the cap as well. Same guard the phone carries. */
    try {
      params.degradationPreference = 'balanced';
      await sender.setParameters(params);
    } catch {
      delete params.degradationPreference;
      await sender.setParameters(params);
    }
    legState.set(uid, { bps, scale });
  } catch (e) { /* a cap that will not apply must never break the call */ }
}

/* 🟢 ONE loop for this member, adjusting every leg from its OWN stats — a peer
   on a bad connection should not drag down everybody else's picture. */
function startGroupRamp() {
  if (rampTimer) return;
  const tick = async () => {
    if (!peers.size) { stopGroupRamp(); return; }
    for (const [uid, pc] of peers) {
      if (pc.connectionState !== 'connected') continue;
      let est = null;
      try {
        const rep = await pc.getStats();
        rep.forEach((r) => {
          if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded'
              && typeof r.availableOutgoingBitrate === 'number') est = r.availableOutgoingBitrate;
        });
      } catch { continue; }
      if (!est) continue;                                   // no opinion → hold
      const prev = legState.get(uid) || { bps: START_BPS, scale: 4 };
      const want = Math.round(est * 0.85);
      /* dead band: a change costs a keyframe, so ignore drift under 8% */
      if (Math.abs(want - prev.bps) < prev.bps * 0.08) continue;
      await applyLegBudget(uid, want);
    }
  };
  /* jittered, so every member of the call does not step at the same instant */
  rampTimer = setInterval(tick, 2000 + Math.floor(Math.random() * 600));
}
function stopGroupRamp() { if (rampTimer) { clearInterval(rampTimer); rampTimer = null; } }

async function newPeerConnection(uid, isInitiator) {
  if (peers.has(uid)) return peers.get(uid);
  const iceServers = await fetchIce();
  /* the app's own configuration, including the pool size and why it is 1 —
     a mesh pre-gathers one full candidate set PER PEER, and forty-nine of the
     fifty a five-person call would gather are thrown away */
  const pc = new RTCPeerConnection({ iceServers, iceCandidatePoolSize: 1, bundlePolicy: 'max-bundle' });
  peers.set(uid, pc);

  if (state.localStream) {
    for (const track of state.localStream.getTracks()) {
      try { pc.addTrack(track, state.localStream); } catch { /* a duplicate track is not fatal */ }
    }
  }

  pc.onicecandidate = (e) => {
    if (!e.candidate) return;
    socket?.emit('group-ice-candidate', { candidate: e.candidate, toUserId: uid, fromUserId: myUid });
  };

  pc.ontrack = (e) => {
    const stream = e.streams[0];
    if (stream) setPeer(uid, { stream });
  };

  /* 🩺 PER-LEG STATE, so one bad connection is repaired rather than evicted. */
  const leg = { downSince: 0, everUp: false, dropAts: [], lastTryAt: 0, timer: null, busy: false };
  legs.set(uid, leg);

  const legTick = async () => {
    leg.timer = null;
    const live = peers.get(uid);
    if (live !== pc) return;                       // stale leg, someone re-made it
    const d = decide({
      state: pc.connectionState, downSince: leg.downSince, now: Date.now(),
      everConnected: leg.everUp, dropAts: leg.dropAts, lastTryAt: leg.lastTryAt,
    });
    if (d.action === 'end') { console.log('[group] leg gave up:', uid.slice(0, 6)); dropPeer(uid); return; }
    if (d.announce) setPeer(uid, { reconnecting: true });
    if (d.action === 'restart' && !leg.busy) {
      leg.busy = true; leg.lastTryAt = Date.now();
      try {
        const offer = await pc.createOffer({ iceRestart: true });
        await pc.setLocalDescription(offer);
        socket?.emit('group-offer', { offer, toUserId: uid, fromUserId: myUid });
        console.log('[group] ICE restart →', uid.slice(0, 6));
      } catch (e) { console.warn('[group] restart failed:', e?.message); }
      finally { leg.busy = false; }
    }
    if (d.action !== 'none') leg.timer = setTimeout(legTick, 1000);
  };

  pc.oniceconnectionstatechange = () => {
    const st = pc.iceConnectionState;
    /* 🟥 THIS DROPPED THE PERSON. `dropPeer(uid)` on 'failed' means a participant
       whose wifi blinks VANISHES from the call — their tile disappears and they
       have to rejoin. `failed` is a lost PATH, not a lost person, and in a mesh
       every leg fails independently: one bad hop should not evict anybody. */
    if (st === 'connected' || st === 'completed') {
      leg.everUp = true;
      if (leg.timer) { clearTimeout(leg.timer); leg.timer = null; }
      leg.downSince = 0; leg.lastTryAt = 0;
      setPeer(uid, { reconnecting: false });
      applyLegBudget(uid);
      startGroupRamp();
      return;
    }
    if (st === 'closed') { dropPeer(uid); return; }   // deliberately gone
    if (st === 'failed' || st === 'disconnected') {
      if (!leg.downSince) {
        leg.downSince = Date.now();
        leg.dropAts = [...leg.dropAts.filter((t) => Date.now() - t < 60000), leg.downSince].slice(-8);
      }
      if (!leg.timer) legTick();
    }
  };

  if (isInitiator) {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    socket?.emit('group-offer', { offer, toUserId: uid, fromUserId: myUid });

    /* 🟥 THE OFFER RETRY IS NOT OPTIONAL, and the app carries the same one. A
       phone answering from the background has a socket mid-reconnect, so the
       very first offer is sometimes dropped — no answer ever comes back and
       that tile stays black for the whole call, because once the peer object
       exists nothing re-offers. Re-send the SAME offer while, and only while,
       we are still waiting: the moment the answer lands signalingState flips to
       'stable' and this stops, so a completed handshake is never disturbed. */
    let tries = 0;
    const timer = setInterval(() => {
      const live = peers.get(uid);
      if (!live || live.signalingState !== 'have-local-offer' || tries >= 4) { clearInterval(timer); return; }
      tries += 1;
      socket?.emit('group-offer', { offer: live.localDescription, toUserId: uid, fromUserId: myUid });
    }, 3000);
  }
  return pc;
}

/**
 * 🟥 THE DETERMINISTIC INITIATOR, COPIED EXACTLY: the peer with the GREATER uid
 *    string offers, the lower one waits. Two consequences, both required:
 *      · exactly one side ever offers, so there is no glare to resolve;
 *      · this is safe to call repeatedly, from the one-shot 'group-user-joined'
 *        AND from every 'group-call-state' broadcast — which is what heals a
 *        lost offer instead of leaving both sides staring at "only me".
 *    Reversing the comparison would not merely swap roles: BOTH clients would
 *    then agree the other one offers, and nobody ever would.
 */
function ensurePeer(uid) {
  if (!uid || uid === myUid) return;
  if (peers.has(uid)) return;
  if (!state.localStream) return;      // nothing to send yet; the next broadcast retries
  if ((myUid || '') > uid) newPeerConnection(uid, true);
  // else: they offer, and the 'group-offer' listener answers it
}

async function getMedia(video) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video: video ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } : false,
  });
  state.localStream = stream;
  if (state.muted) stream.getAudioTracks().forEach((t) => { t.enabled = false; });
  if (state.cameraOff) stream.getVideoTracks().forEach((t) => { t.enabled = false; });
  emit();
  return stream;
}

/* ══ THE WIRE ═══════════════════════════════════════════════════════════════ */

export function attachGroupCalls(uid, profile) {
  myUid = String(uid || '');
  /* 🟥 LAST KNOWN GOOD, NOT `profile || null`. The profile loads a beat after
     login, so the first attach usually passes null — and this function is called
     again once it arrives. Overwriting with null on any later call would put the
     name back to "Me" in everyone else's participant tiles. */
  if (profile) myProfile = profile;
  const s = getSocket();
  if (!s || s === bound) return;
  bound = s;
  socket = s;

  s.on('incoming-group-call', (p) => {
    if (!p?.groupId) return;
    /* 🟥 NEVER RING YOURSELF. The server fans the invite to the whole member
       list, and on a bad day that includes the caller's own other sockets. The
       phone carries the identical guard (HomeScreen: "Loopback prevention"). */
    if (String(p.fromUserId || p.callerId || '') === myUid) return;
    /* 🟥 THE SAME DEDUP KEY THE PHONE USES — groupId + when the call started.
       The server redelivers `incoming-group-call` on socket reconnect, so
       without this one call rings twice, and a ring that restarts after being
       declined cannot be declined again. A RE-INVITE deliberately clears the
       entry, because being rung again is the whole point of one. */
    /* 🟥 …AND WHICH RING. A re-invite is the same call, so it had the same key as
       the ring just declined; the server numbers rings now (`inviteSeq`), and the
       phone uses the same key from the same shared table. An un-numbered re-invite
       (older server) still clears the old entry, as before. */
    const key = groupInviteKey(p.groupId, p.callStartedAt || p.startedAt || '', p.inviteSeq);
    if (groupInviteLegacyReinvite(p)) answered.delete(key);
    else if (answered.has(key)) return;

    if (state.groupId === p.groupId && state.status !== 'idle') return;
    if (state.status === 'active') return;    // busy here; the phones can take it
    ringKey = key;
    ringSeq = groupInviteSeq(p);
    state.status = 'ringing';
    state.groupId = String(p.groupId);
    state.groupName = p.groupName || 'Group';
    state.callType = p.callType === 'video' ? 'video' : 'voice';
    state.callerId = p.callerId || p.fromUserId || null;
    state.callerName = p.callerName || '';
    state.callerImage = p.callerImage || null;
    state.sessionId = p.sessionId || null;
    state.note = 'Incoming group call';
    emit();
    /* the invitee's client tells the server its ringing UI is up — this is what
       moves the tile from 'calling' to 'ringing' on everyone else's screen */
    s.emit('group-call-ringing-ack', { groupId: state.groupId, userId: myUid });
    ring(true);
    /* \U0001f7e5 AND IT GIVES UP WHEN THE PHONE WOULD. The app declines at 30s
       (HomeScreen's timeout), and the server's own invite timeout is 30s too — a
       browser that rang past it would be answering a call the server has already
       written off as missed. */
    if (ringTimer) clearTimeout(ringTimer);
    ringTimer = setTimeout(() => { if (state.status === 'ringing') declineGroupCall(); }, 30000);
  });

  /* \U0001f7e5 ANSWERED, OR ENDED, SOMEWHERE ELSE. cancelRingOnOtherDevices emits
     `call-ended` with the call id `group_<groupId>` to every OTHER socket of the
     account the moment one device picks up — which is exactly what stops this
     browser ringing after the call was taken on a phone. */
  s.on('call-ended', ({ callId } = {}) => {
    if (!state.groupId) return;
    if (String(callId || '') !== `group_${state.groupId}`) return;
    if (state.status === 'ringing') endLocal('Answered on another device');
  });

  s.on('group-user-joined', ({ newUserId, userData } = {}) => {
    if (!newUserId || newUserId === myUid) return;
    if (userData) setPeer(newUserId, { name: userData.name || userData.fullName || '', image: userData.image || userData.profileImage || null });
    ensurePeer(newUserId);
  });

  s.on('group-offer', async ({ offer, fromUserId } = {}) => {
    if (!offer || !fromUserId) return;
    /* 🟢 RE-OFFER SAFE, the same three cases the app handles: a connected peer
       means the retry above is chasing a call that is already up — ignore it.
       Anything else is a half-open leftover, so close it and answer the fresh
       offer cleanly rather than bailing out silently. */
    const existing = peers.get(fromUserId);
    if (existing) {
      const st = existing.iceConnectionState;
      if (st === 'connected' || st === 'completed') return;
      try { existing.close(); } catch { /* already closed */ }
      peers.delete(fromUserId);
    }
    const pc = await newPeerConnection(fromUserId, false);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    await flushIce(fromUserId, pc);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socket?.emit('group-answer', { answer, toUserId: fromUserId, fromUserId: myUid });
  });

  s.on('group-answer', async ({ answer, fromUserId } = {}) => {
    const pc = peers.get(fromUserId);
    if (!pc || !answer) return;
    /* 🟥 ONLY WHILE WE ARE ACTUALLY WAITING FOR ONE. The offer retry means a
       second answer can arrive after the first has settled the connection, and
       setRemoteDescription on a 'stable' connection throws — which, unhandled
       inside a socket callback, takes the whole handler down with it. */
    if (pc.signalingState !== 'have-local-offer') return;
    await pc.setRemoteDescription(new RTCSessionDescription(answer));
    await flushIce(fromUserId, pc);
  });

  s.on('group-ice-candidate', async ({ candidate, fromUserId } = {}) => {
    if (!candidate || !fromUserId) return;
    const pc = peers.get(fromUserId);
    /* 🟥 CANDIDATES OUTRUN THE DESCRIPTION. The server relays ICE the moment it
       has it, so a candidate routinely lands before setRemoteDescription — and
       addIceCandidate before there is a remote description throws. Holding them
       is the difference between a call that connects and one that connects only
       when the timing happens to be kind. */
    if (!pc || !pc.remoteDescription) {
      const q = pendingIce.get(fromUserId) || [];
      q.push(candidate);
      pendingIce.set(fromUserId, q);
      return;
    }
    try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch { /* a stale candidate is not fatal */ }
  });

  s.on('group-user-left', ({ userId } = {}) => {
    if (!userId) return;
    dropPeer(userId);
    /* 🟥 THIS USED TO END THE CALL WHEN THE LAST PEER LEFT, AND THAT IS NOT WHAT A
       GROUP CALL IS. "A dock showing only yourself is a mirror, not a call" reads
       well and is wrong: a group call with one person in it is a room that is
       still open, and the whole point of a room is that people come back to it.
       Whoever started it hanging up does not close it — the app knows this, which
       is why the phone still offered Join from the chat header while the browser
       had already torn itself down and left the account in a call it could no
       longer see.

       🟢 THE CALL IS OVER WHEN THE SERVER SAYS IT IS. `chat.activeCall` going away
          is the authoritative signal and syncActiveCall already acts on it, so
          removing this leaves the ending to the one thing that actually knows —
          rather than to an inference from who happens to be connected right now. */
    emit();
  });

  s.on('group-call-state', (p) => {
    if (!p?.groupId || p.groupId !== state.groupId) return;
    state.invitees = p.invitees || {};
    state.callerId = p.callerId || state.callerId;
    if (Number(p.maxInvites) > 0) state.maxInvites = Number(p.maxInvites);
    emit();
    /* 🟥 THE SELF-HEALING PASS. This broadcast repeats, so it is the retry for
       an offer that went missing while somebody's socket was reconnecting. */
    if (state.status === 'active') {
      for (const [uid, info] of Object.entries(state.invitees)) {
        if (info?.state === 'joined') ensurePeer(uid);
      }
    }
  });

  s.on('group-peer-mute-change', ({ userId, muted } = {}) => {
    if (userId && state.peers[userId]) setPeer(userId, { muted: !!muted });
  });
}

async function flushIce(uid, pc) {
  const q = pendingIce.get(uid);
  if (!q?.length) return;
  pendingIce.delete(uid);
  for (const c of q) {
    try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* stale */ }
  }
}

/* ══ WHAT THE UI CALLS ══════════════════════════════════════════════════════ */

/** Ring a group. `chat` is the chat document; `me` is my own profile. */
export async function startGroupCall(chat, isVideo, me) {
  const s = getSocket();
  if (!s || !chat?.id) return;
  const reason = groupCallBlockedReason(chat, myUid);
  /* 🟥 CHECKED HERE TOO, not only where the button is drawn. A disabled button
     is a courtesy; this is the rule. The permission can also change between the
     render and the click, and the person who turned it off is entitled to have
     that mean something. */
  if (reason) { state.error = reason; emit(); return; }

  const participantIds = (chat.participants || [])
    .filter((u) => u && !(chat.leftParticipants || []).includes(u));

  state.status = 'joining';
  state.groupId = chat.id;
  state.groupName = chat.name || chat.groupName || 'Group';
  state.callType = isVideo ? 'video' : 'voice';
  state.callerId = myUid;
  iAmHost = true;
  state.note = 'Starting…';
  state.error = '';
  emit();

  try {
    await getMedia(isVideo);
  } catch {
    state.error = isVideo ? 'No camera or microphone' : 'No microphone';
    endLocal('');
    return;
  }

  s.emit('start-group-call', {
    groupId: chat.id,
    groupName: state.groupName,
    participantIds,
    callerName: me?.fullName || me?.username || 'Someone',
    callerImage: me?.profileImage || null,
    callType: state.callType,
    fromUserId: myUid,
    /* 🌐 WHICH DEVICE RANG. The server stores it on activeCall and puts it on
       the finished-call record, so the conversation can say "started a video
       call · from the web" on every device instead of leaving people to work
       out why the host is not on their phone. */
    startedFrom: 'web',
  });

  await joinRoom();
}

/** Accept a ring. */
export async function joinGroupCall(chat) {
  if (state.status !== 'ringing' || !state.groupId) return;
  state.status = 'joining';
  state.note = 'Joining…';
  emit();
  try {
    await getMedia(state.callType === 'video');
  } catch {
    state.error = 'No microphone';
    declineGroupCall();
    return;
  }
  await joinRoom(chat);
}

/* 🟥 THE HOST WRITES THE SESSION ID, EVERYONE ELSE READS IT — the phone's rule
   (GroupCallScreen.initializeSession), and the reason both sides land in the same
   `call_threads` document. Reading first is not an optimisation: generating one
   when a call is already running is what splits the in-call chat in two. */
async function resolveSession(chat) {
  const ref = doc(db, 'chats', state.groupId);
  const existing = chat?.activeCall?.sessionId || state.sessionId;
  if (existing) { state.sessionId = existing; return existing; }
  const fresh = `call_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  state.sessionId = fresh;
  /* merge, so this cannot clobber the participants the server maintains */
  try { await setDoc(ref, { activeCall: { sessionId: fresh } }, { merge: true }); } catch { /* the call still works without a chat thread */ }
  return fresh;
}

/** The in-call chat, live. Same path and same fields the phone writes. */
function watchCallChat() {
  if (chatUnsub) { try { chatUnsub(); } catch { /* already gone */ } chatUnsub = null; }
  if (!state.groupId || !state.sessionId) return;
  const q = query(
    collection(db, 'chats', state.groupId, 'call_threads', state.sessionId, 'messages'),
    orderBy('timestamp', 'asc'),
  );
  chatUnsub = onSnapshot(q, (snap) => {
    state.chat = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    emit();
  }, () => { /* a rules failure must not take the call down with it */ });
}

export async function sendCallMessage(text) {
  const body = String(text || '').trim();
  if (!body || !state.groupId || !state.sessionId) return;
  try {
    await addDoc(collection(db, 'chats', state.groupId, 'call_threads', state.sessionId, 'messages'), {
      text: body,
      senderId: myUid,
      senderName: myProfile?.fullName || myProfile?.username || 'User',
      senderImage: myProfile?.profileImage || null,
      timestamp: serverTimestamp(),
    });
  } catch { /* the send failed; the input keeps the text */ }
}

/**
 * Ring somebody again who declined or never picked up.
 *
 * 🟥 THE COUNT IS THE SERVER'S, NOT OURS. index.js keeps `inviteCount` per
 *    invitee in `groupCallStates` and refuses past MAX_GROUP_REINVITES, then
 *    publishes the number on every `group-call-state`. So this cannot drift from
 *    the phone and there is nothing to reconcile: re-invite twice here and once
 *    there, and the third is the last, because both clients are reading and
 *    incrementing one counter.
 */
export function reinvite(uid) {
  if (!uid || state.status !== 'active') return;
  getSocket()?.emit('group-call-reinvite', { groupId: state.groupId, targetUserId: uid });
}

/** How many rings this person has left, from the server's own tally. */
export function invitesLeft(uid) {
  const info = state.invitees[uid];
  if (!info) return 0;
  return Math.max(0, state.maxInvites - (Number(info.inviteCount) || 0));
}

async function joinRoom(chat) {
  const s = getSocket();
  if (!s || !state.groupId) return;
  ring(false);
  if (ringTimer) { clearTimeout(ringTimer); ringTimer = null; }
  if (ringKey) answered.add(ringKey);
  await resolveSession(chat);
  s.emit('join-group-call', {
    groupId: state.groupId,
    userId: myUid,
    userData: {
      name: myProfile?.fullName || myProfile?.username || 'Me',
      image: myProfile?.profileImage || null,
    },
  });
  /* the app writes itself into activeCall.participants on join; the phones read
     that to decide whether a call is live, so a browser that skipped it would be
     invisible to them */
  try {
    await setDoc(doc(db, 'chats', state.groupId), {
      activeCall: { participants: arrayUnion(myUid) },
    }, { merge: true });
  } catch { /* the socket join is what actually puts us in the call */ }

  state.status = 'active';
  state.startedAt = Date.now();
  state.note = '';
  emit();
  watchCallChat();

  /* anyone already inside whose uid is lower than mine is waiting for MY offer */
  const inside = Object.entries(state.invitees)
    .filter(([, i]) => i?.state === 'joined')
    .map(([u]) => u);
  for (const uid of inside) ensurePeer(uid);
  if (chat?.participants) for (const uid of chat.participants) ensurePeer(uid);
}

export function declineGroupCall() {
  const s = getSocket();
  if (ringKey) answered.add(ringKey);
  if (state.groupId) s?.emit('group-call-decline', { groupId: state.groupId, userId: myUid, ...(ringSeq ? { inviteSeq: String(ringSeq) } : {}) });
  endLocal('');
}

export function leaveGroupCall() {
  const s = getSocket();
  /* 🟢 the bubble first, while the ids are still in hand — endLocal clears them */
  if (iAmHost) writeCallHistoryMessage();
  if (state.groupId) s?.emit('leave-group-call', { groupId: state.groupId, userId: myUid });
  endLocal('');
}

/* ═══════════════════════════════════════════════════════════════════════════
   🧾 "📞 Abdullah started a Video call" — the bubble the conversation keeps.

   The phone writes exactly this document when the HOST leaves the call
   (GroupCallScreen.saveGroupCallHistory), and every surface that renders a call
   — the chat, the call list, and the row that opens what was said during it —
   reads it. A call hosted from a browser wrote nothing at all, so it left no
   trace in the conversation on ANY device.

   🟥 THE ID IS DERIVED FROM THE SESSION, not from the clock. The phone uses
      `sys_msg_${Date.now()}`, which is fine there because only one client ever
      writes it — but a derived id also makes a retry idempotent, and it is the
      spelling every reader's dedup already understands (`call_end_<session>`,
      see CallsPanel).
   ═══════════════════════════════════════════════════════════════════════════ */
async function writeCallHistoryMessage() {
  const groupId = state.groupId;
  const sessionId = state.sessionId;
  if (!groupId || !sessionId) return;
  const kind = state.callType === 'video' ? 'Video' : 'Voice';
  const who = myProfile?.fullName || myProfile?.username || 'Group Member';
  const started = state.startedAt || Date.now();
  const id = `call_end_${sessionId}`;
  const participants = Object.keys(state.invitees).length
    ? Object.keys(state.invitees)
    : [myUid, ...Object.keys(state.peers)];
  try {
    await setDoc(doc(db, 'chats', groupId, 'messages', id), {
      _id: id,
      text: `📞 ${who} started a ${kind} call`,
      createdAt: new Date(),
      user: { _id: 'system', name: 'System' },
      type: 'system',
      systemType: 'call_history',
      timestamp: serverTimestamp(),
      senderName: who,
      senderId: myUid,
      isGroup: true,
      callData: {
        callType: state.callType === 'video' ? 'video' : 'voice',
        duration: Math.max(0, Math.round((Date.now() - started) / 1000)),
        answered: true,
        callInitiatedBy: myUid,
        startedFrom: 'web',
        participants,
        /* the thread the in-call chat was written to — this is what makes the
           finished row open what was said during the call */
        callSessionId: sessionId,
      },
    }, { merge: true });
  } catch { /* the call still happened; the bubble is the record of it */ }

  /* 🟥 AND KEEP OUR OWN COPY. The document above is a relay: the first phone in
     this group to ingest it deletes it from Firestore, by design, so a browser
     relying on it watches its own row vanish a few seconds after writing it.
     Written here rather than waiting for the server's broadcast so the row is
     in the conversation the instant the call ends. */
  try {
    const row = callRowFrom({
      chatId: groupId,
      sessionId,
      callType: state.callType,
      duration: Math.max(0, Math.round((Date.now() - started) / 1000)),
      callInitiatedBy: myUid,
      starterName: who,
      participants,
      startedFrom: 'web',
      at: Date.now(),
    });
    if (row) rememberCallRow(groupId, row);
  } catch { /* the Firestore copy above is still out there */ }
}

function endLocal(note) {
  /* 🟥 THE RAMP AND EVERY LEG TIMER MUST DIE WITH THE CALL. A leg timer left
     running restarts ICE on a closed connection for somebody who has left, and
     the ramp keeps polling getStats on peers that no longer exist. */
  stopGroupRamp();
  for (const [, leg] of legs) if (leg?.timer) { try { clearTimeout(leg.timer); } catch { /* gone */ } }
  legs.clear();
  legState.clear();
  pinnedUid = null;
  ring(false);
  if (ringTimer) { clearTimeout(ringTimer); ringTimer = null; }
  if (chatUnsub) { try { chatUnsub(); } catch { /* already gone */ } chatUnsub = null; }
  ringKey = '';
  ringSeq = 0;
  sawActiveCall = false;
  iAmHost = false;
  for (const uid of [...peers.keys()]) dropPeer(uid);
  peers.clear();
  pendingIce.clear();
  if (state.localStream) {
    /* 🟥 STOP THE TRACKS, not just the reference. A MediaStream that is merely
       dereferenced keeps the camera light on until garbage collection, which
       reads to the person as "it is still watching me". */
    for (const t of state.localStream.getTracks()) { try { t.stop(); } catch { /* already stopped */ } }
  }
  state.status = 'idle';
  state.groupId = null;
  state.groupName = '';
  state.callerId = null;
  state.callerName = '';
  state.callerImage = null;
  state.peers = {};
  state.invitees = {};
  state.localStream = null;
  state.startedAt = null;
  state.muted = false;
  state.cameraOff = false;
  state.sessionId = null;
  state.chat = [];
  state.note = note || '';
  emit();
}

export function toggleGroupMute() {
  state.muted = !state.muted;
  state.localStream?.getAudioTracks().forEach((t) => { t.enabled = !state.muted; });
  getSocket()?.emit('group-peer-mute-change', { groupId: state.groupId, userId: myUid, muted: state.muted });
  emit();
}

export function toggleGroupCamera() {
  state.cameraOff = !state.cameraOff;
  state.localStream?.getVideoTracks().forEach((t) => { t.enabled = !state.cameraOff; });
  emit();
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 ONE FIELD DECIDES WHETHER A CALL IS STILL HAPPENING, and it is the server's:
   `chats/{id}.activeCall`. The leave handler deletes it the moment the socket
   room empties, so it is already the answer to "did somebody hang up".

   That is what makes hanging up on the phone dismiss the browser, and hanging up
   in the browser dismiss the phone: neither is telling the other anything. Both
   are watching the same field, which the server maintains, so there is no
   message to lose and no order to get wrong.

   🟥 IT ALSO COVERS THE CASE NOTHING ELSE DOES: a caller who gives up before
      anybody answers. The ringing invitee is not in the socket room yet — that
      is what joining means — so no room broadcast can reach them, and without
      this they would ring for the full thirty seconds at a call that no longer
      exists.

   🟢 THE LATCH IS WHY IT IS SAFE. `activeCall` appears a moment AFTER the caller
      emits start-group-call, so "no activeCall means over" would end every call
      in the instant before the server wrote it. We only act once the field has
      actually been seen.
   ═══════════════════════════════════════════════════════════════════════════ */
/** Milliseconds from any shape `activeCall.startedAt` has been written in. */
function startedAtMs(v) {
  try {
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v === 'number') return v;
    if (typeof v === 'string') { const p = Date.parse(v); return Number.isFinite(p) ? p : 0; }
    if (typeof v.seconds === 'number') return v.seconds * 1000;
  } catch { /* a shape nobody has written yet */ }
  return 0;
}

export function syncActiveCall(chats) {
  if (state.status === 'idle' || !state.groupId) return;
  const chat = (chats || []).find((c) => c.id === state.groupId);
  if (!chat) return;                       // not in my list: no opinion
  const live = !!chat.activeCall;
  if (live) {
    sawActiveCall = true;
    /* 🕒 THE CALL'S CLOCK, NOT THIS BROWSER'S. `state.startedAt` was set to the
       moment THIS tab joined, so a browser joining a call already ten minutes
       old showed 00:00 and then wrote a ten-minutes-short duration when it
       hung up. `activeCall.startedAt` is the server's single stamp, which is
       what the phone reads too — so both clients report the same length.
       Only ever moved EARLIER, so a stale or malformed value cannot shorten a
       call that is demonstrably already running. */
    const shared = startedAtMs(chat.activeCall.startedAt);
    if (shared > 0 && (!state.startedAt || shared < state.startedAt)) {
      state.startedAt = shared;
      emit();
    }
    /* a joiner that arrived before the host wrote the id picks it up here */
    if (!state.sessionId && chat.activeCall.sessionId) {
      state.sessionId = chat.activeCall.sessionId;
      emit();
      if (state.status === 'active') watchCallChat();
    }
    return;
  }
  if (sawActiveCall) endLocal(state.status === 'ringing' ? '' : 'Call ended');
}

export { MAX_MESH, peerName };
